import { CurrencyCode } from "#graphql/supergraphEnums";
import { type MoneyType } from "../../Money/types";
import { type CashTransaction } from "./types";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 * Stub fixtures for CashTransactionsTable (M1, no backend).
 * ─────────────────────────────────────────────────────────────────────────────
 * These back the stub datasource, Storybook stories, and tests. At switchover
 * they are replaced by codegen'd MSW handlers / real subgraph data (LLD
 * "Stub-hook strategy"). Colocated with the component (not under `__tests__`)
 * since the stub datasource imports them and ships in the built package.
 */

const usd = (amount: number): MoneyType => ({
  amount,
  currency: CurrencyCode.Usd,
  precision: 2,
});

type SeedEntity = {
  entityName: string;
  bankName: string;
  bankAccountId: string;
  bankAccountLast4: string;
};

const SEED_ACCOUNTS: SeedEntity[] = [
  {
    entityName: "Acme Growth Fund I",
    bankName: "JP Morgan Chase",
    bankAccountId: "ba-1001",
    bankAccountLast4: "1001",
  },
  {
    entityName: "Acme Growth Fund II",
    bankName: "Morgan Stanley",
    bankAccountId: "ba-2002",
    bankAccountLast4: "2002",
  },
  {
    entityName: "Beacon Real Estate Fund",
    bankName: "Wells Fargo",
    bankAccountId: "ba-3003",
    bankAccountLast4: "3003",
  },
];

const DESCRIPTIONS = [
  "Capital call receipt",
  "Management fee",
  "Wire transfer out",
  "Distribution payment",
  "Interest income",
  "Bank service charge",
];

/**
 * Deterministically generates a set of cash transactions spread across the seed
 * accounts. Running balance walks down from a starting value so rows look
 * realistic; amounts alternate sign. Deterministic so tests/stories are stable.
 *
 * @param count - How many transactions to generate.
 */
export const generateCashTransactions = (count = 60): CashTransaction[] => {
  const transactions: CashTransaction[] = [];
  const runningBalanceByAccount = new Map<string, number>();

  for (let i = 0; i < count; i += 1) {
    const account = SEED_ACCOUNTS[i % SEED_ACCOUNTS.length];
    const isCredit = i % 2 === 0;
    const magnitude = ((i % 7) + 1) * 125000; // minor units
    const signedAmount = isCredit ? magnitude : -magnitude;

    const prevBalance = runningBalanceByAccount.get(account.bankAccountId) ?? 100_000_000;
    const nextBalance = prevBalance + signedAmount;
    runningBalanceByAccount.set(account.bankAccountId, nextBalance);

    // Descending dates so the default (postDate desc) sort looks natural.
    const day = String(28 - (i % 28)).padStart(2, "0");
    const month = String((i % 12) + 1).padStart(2, "0");

    transactions.push({
      id: `cash-txn-${i}-${account.bankAccountId}`,
      postDate: `2026-${month}-${day}`,
      entityName: account.entityName,
      bankName: account.bankName,
      bankAccountLast4: account.bankAccountLast4,
      bankAccountId: account.bankAccountId,
      description: DESCRIPTIONS[i % DESCRIPTIONS.length],
      amount: usd(signedAmount),
      balance: usd(nextBalance),
    });
  }

  return transactions;
};

/** A small fixed set of transactions for a single account (used in tests). */
export const singleAccountTransactions: CashTransaction[] = [
  {
    id: "cash-txn-a",
    postDate: "2026-06-25",
    entityName: "Acme Growth Fund I",
    bankName: "JP Morgan Chase",
    bankAccountLast4: "1001",
    bankAccountId: "ba-1001",
    description: "Capital call receipt",
    amount: usd(5_000_000),
    balance: usd(105_000_000),
  },
  {
    id: "cash-txn-b",
    postDate: "2026-06-20",
    entityName: "Acme Growth Fund I",
    bankName: "JP Morgan Chase",
    bankAccountLast4: "1001",
    bankAccountId: "ba-1001",
    description: "Management fee",
    amount: usd(-250_000),
    balance: usd(100_000_000),
  },
];

/** The bankAccountId that `singleAccountTransactions` all belong to. */
export const SINGLE_ACCOUNT_ID = "ba-1001";

/** The default stub dataset used by the datasource when none is injected. */
export const defaultCashTransactions: CashTransaction[] = generateCashTransactions();
